import React, { useState, useEffect } from "react";
import "./stats.css";
import axiosInstance from "../../../api/axios";

const Stats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axiosInstance.get("/api/applications/stats");
        setStats(response.data);
      } catch (error) {
        setError("Error fetching statistics");
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const handleDownload = async ({
    filter,
    path = "/api/applications/export",
    filename = "applications.csv",
  }) => {
    let url;
    let link;
    try {
      setError("");
      const response = await axiosInstance.get(path, {
        params: filter ? { filters: JSON.stringify(filter) } : undefined,
        responseType: "blob",
      });
      url = window.URL.createObjectURL(new Blob([response.data]));
      link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
    } catch (error) {
      let message = error.response?.data?.message;
      if (error.response?.data instanceof Blob) {
        try {
          const body = JSON.parse(await error.response.data.text());
          message = body.message;
        } catch {
          message = null;
        }
      }
      setError(message || "Error downloading CSV");
    } finally {
      link?.remove();
      if (url) window.URL.revokeObjectURL(url);
    }
  };

  const statValue = (value) => {
    if (loading) return "Loading...";
    if (typeof value === "number") return value.toLocaleString();
    return value ?? "--";
  };

  const cards = [
    {
      title: "Registered Hackers",
      category: "Google Sheets",
      count: statValue(stats?.total_confirmed),
      path: "/api/applications/export/registered-hackers",
      filename: "registered-hackers.csv",
      action: "Export confirmed hackers",
    },
    {
      title: "Total Submitted",
      category: "Submitted",
      count: statValue(stats?.total_applications),
      filter: { status: { $ne: "started" } },
      action: "Export CSV",
    },
    {
      title: "Princeton Submitted",
      category: "Submitted",
      count: statValue(stats?.princeton_applications),
      filter: { school: "Princeton University", status: { $ne: "started" } },
      action: "Export CSV",
    },
    {
      title: "Non-Princeton Submitted",
      category: "Submitted",
      count: statValue(stats?.non_princeton_applications),
      filter: {
        school: { $ne: "Princeton University" },
        status: { $ne: "started" },
      },
      action: "Export CSV",
    },
    {
      title: "Total Started",
      category: "Started",
      count: statValue(stats?.total_started),
      filter: { status: "started" },
      action: "Export CSV",
    },
    {
      title: "Princeton Started",
      category: "Started",
      count: statValue(stats?.princeton_started),
      filter: { school: "Princeton University", status: "started" },
      action: "Export CSV",
    },
    {
      title: "Non-Princeton Started",
      category: "Started",
      count: statValue(stats?.non_princeton_started),
      filter: {
        school: { $ne: "Princeton University" },
        status: "started",
      },
      action: "Export CSV",
    },
    {
      title: "Princeton Accepted",
      category: "Accepted",
      count: statValue(stats?.princeton_accepted),
      filter: {
        school: "Princeton University",
        status: { $in: ["accepted", "waitlist_accepted", "confirmed"] },
      },
      action: "Export CSV",
    },
    {
      title: "Non-Princeton Accepted",
      category: "Accepted",
      count: statValue(stats?.non_princeton_accepted),
      filter: {
        school: { $ne: "Princeton University" },
        status: { $in: ["accepted", "waitlist_accepted", "confirmed"] },
      },
      action: "Export CSV",
    },
    {
      title: "Princeton Confirmed",
      category: "Confirmed",
      count: statValue(stats?.princeton_confirmed),
      filter: { school: "Princeton University", status: "confirmed" },
      action: "Export CSV",
    },
    {
      title: "Non-Princeton Confirmed",
      category: "Confirmed",
      count: statValue(stats?.non_princeton_confirmed),
      filter: { school: { $ne: "Princeton University" }, status: "confirmed" },
      action: "Export CSV",
    },
    {
      title: "Princeton Declined",
      category: "Declined",
      count: statValue(stats?.princeton_declined),
      filter: { school: "Princeton University", status: "declined" },
      action: "Export CSV",
    },
    {
      title: "Non-Princeton Declined",
      category: "Declined",
      count: statValue(stats?.non_princeton_declined),
      filter: { school: { $ne: "Princeton University" }, status: "declined" },
      action: "Export CSV",
    },
  ];

  const exportDeadline = stats?.registered_hackers_export_due;
  const exportDeadlineLabel = exportDeadline
    ? new Date(`${exportDeadline}T00:00:00`).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  return (
    <div className="dashboard stats-page">
      <div className="stats-header">
        <h1 className="ad-dash-title stats-title">HackPrinceton Statistics</h1>
        <p className="stats-subtitle">
          {exportDeadlineLabel
            ? `Export the registered hacker list by ${exportDeadlineLabel}, 10 days before the hackathon starts.`
            : "Set the hackathon start date to show the registered hacker export deadline."}{" "}
          All CSV files are compatible with Google Sheets.
        </p>
      </div>
      <div className="stats-cards-div">
        {error && <span className="stats-error">{error}</span>}
        <div className="stats-cards">
          {cards.map((card, index) => (
            <button
              key={index}
              type="button"
              className="stats-card"
              onClick={() => handleDownload(card)}
              aria-label={`Export ${card.title} as CSV`}
            >
              <span className="stats-category">{card.category}</span>
              <h3>{card.title}</h3>
              <p>{card.count}</p>
              <span className="stats-card-action">{card.action}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Stats;
